/**
 * phoneUtils.js
 * 
 * Single source of truth for phone number normalization across the frontend.
 * Always compose the full international number as: countryCode + phone digits.
 */

/**
 * Normalize a phone number by combining country code (DDI) + phone (DDD + number).
 * 
 * @param {string} phone - Phone number (DDD + number, e.g. "11994686881")
 * @param {string} countryCode - Country code / DDI (e.g. "55" for Brazil)
 * @returns {string} Normalized phone number (e.g. "5511994686881"), or empty string if invalid
 */
export function normalizePhone(phone, countryCode = '') {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';

  const cc = String(countryCode || '').replace(/\D/g, '');

  // If an explicit country code is provided, prepend it
  if (cc) {
    // Only consider that digits already contain the country code if:
    // 1. digits start with the country code, AND
    // 2. the remaining part (without cc) is long enough to be a full phone number (≥ 10 digits)
    //    This avoids false positives when the DDD happens to match the country code (e.g. DDD 55 in Brazil)
    if (digits.startsWith(cc) && digits.length - cc.length >= 10) {
      return digits;
    }
    return cc + digits;
  }

  // No country code provided — return digits as-is (don't guess/hardcode)
  return digits;
}

/**
 * Format a Brazilian phone (DDD + number) for display: "(11) 99468-6881".
 * Returns the original value when it is not 10 or 11 digits long.
 *
 * @param {string} phone - Phone number (DDD + number)
 * @returns {string}
 */
export function formatPhone(phone = '') {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}
